import { differenceInCalendarDays, isValid } from 'date-fns';
import { parseFlexibleDate } from '@ourbranch/date-fns-helpers';

/**
 * Guards against mailing or emailing policy documents for a term whose effective date is too
 * far in the future.
 *
 * Renewal declaration packets (and other term documents) are generated well before the term
 * becomes effective. Mailing them as soon as they exist means a member receives paperwork for a
 * term that has not started yet — which happened in production: Forreston renewal packets were
 * mailed months early via send-paper-docs-cron (policy 100054759).
 *
 * The hold window is brand-specific. The boundary is strictly-greater-than: a term effective
 * *exactly* at the limit still mails. Notices about the current/ending term (cancellations,
 * non-renewals, FCRA adverse-action) have a past effective date, so they are never held by this
 * guard — no document-type carve-out is needed.
 *
 * Brand is read from the policy-id paper-state extension (the trailing `-<XX>` token; see
 * @ourbranch/brand-config-be getPaperStateExtension):
 *   - CFM (`-CF`): 45 days
 *   - Forreston (`-FM`) / Oakwood (`-OW`): 60 days
 *   - Branch (the default — any other suffix; Branch emits `-BX`/`-BE`/`-SC`): 90 days. Branch
 *     mails very early in some states (e.g. KY), so it gets the widest window.
 */
export const MAILING_HOLD_DAYS_DEFAULT = 90;
export const MAILING_HOLD_DAYS_MUTUAL = 60;
export const MAILING_HOLD_DAYS_CFM = 45;

const CFM_PAPER_EXTENSION = 'CF';
// Forreston + Oakwood share the 60-day window. Branch (everything else) uses the default.
const MUTUAL_60_DAY_PAPER_EXTENSIONS = new Set(['FM', 'OW']);

/**
 * Number of days before a term's effective date that we may begin mailing/emailing its
 * documents, based on the policy-id brand suffix.
 */
export function getMailingHoldDaysForPolicyId(policyId: string): number {
  const paperExtension = policyId?.split('-').pop();
  if (paperExtension === CFM_PAPER_EXTENSION) {
    return MAILING_HOLD_DAYS_CFM;
  }
  if (paperExtension && MUTUAL_60_DAY_PAPER_EXTENSIONS.has(paperExtension)) {
    return MAILING_HOLD_DAYS_MUTUAL;
  }
  return MAILING_HOLD_DAYS_DEFAULT;
}

function toDateAtMidnight(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  // Reuse the platform's shared string parser so this guard's date handling can't drift from the
  // rest of the codebase. parseFlexibleDate treats yyyy-MM-dd as local midnight (also accepts ISO
  // datetimes and MM/dd/yyyy) and returns null on unparseable input; Date inputs normalize
  // directly. The pure calendar-day comparison below is then unaffected by any time-of-day.
  const parsed = typeof value === 'string' ? parseFlexibleDate(value) : new Date(value);
  if (!parsed || !isValid(parsed)) {
    return null;
  }
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

interface TermTooFarArgs {
  policyId: string;
  effectiveDate: string | Date | null | undefined;
  today?: Date;
}

/**
 * Core check shared by the mailing and billing guards: is the term's effective date more than the
 * brand's hold window past `today`? Returns false when the effective date is within the window, in
 * the past, or missing/unparseable — so callers never act (hold a send / skip a bill) on bad data.
 */
function isTermEffectiveBeyondHoldWindow({ policyId, effectiveDate, today = new Date() }: TermTooFarArgs): boolean {
  const effective = toDateAtMidnight(effectiveDate);
  if (!effective) {
    return false;
  }

  const todayAtMidnight = new Date(today);
  todayAtMidnight.setHours(0, 0, 0, 0);

  const daysUntilEffective = differenceInCalendarDays(effective, todayAtMidnight);
  return daysUntilEffective > getMailingHoldDaysForPolicyId(policyId);
}

/**
 * Returns true when the term is effective too far in the future to mail/email its documents yet
 * (the send should be held). Returns false — i.e. allow the send — when the effective date is
 * within the brand window, in the past, or missing/unparseable. We never block a send on bad
 * data, because a false hold could suppress a legally-required current-term notice; the
 * over-send this guards against only occurs with a valid, far-future effective date.
 *
 * @param policyId       Policy id — its trailing `-<XX>` suffix selects the brand window.
 * @param effectiveDate  Effective (start) date of the term the document is for (Date or
 *                       yyyy-MM-dd string).
 * @param today          Defaults to the current date; injectable for testing.
 */
export function isTermTooFarInFutureToMail(args: TermTooFarArgs): boolean {
  return isTermEffectiveBeyondHoldWindow(args);
}

/**
 * Returns true when a policy-change should NOT attempt immediate billing (charge or refund) yet
 * because the term's effective date is too far in the future. Billing intentionally reuses the
 * SAME per-brand window as paper-doc mailing (getMailingHoldDaysForPolicyId): a migrated Forreston
 * farm term ~68 days out was billed at endorsement and failed with "No customer id in policy"
 * (#34404). Returns false — allow billing — for a current/in-force term (effective today or in the
 * past) or when the effective date is missing/unparseable. Same signature as the mailing guard.
 */
export function isTermTooFarInFutureToBill(args: TermTooFarArgs): boolean {
  return isTermEffectiveBeyondHoldWindow(args);
}
